import { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { analyticsService } from '@/services/analyticsService'
import toast from 'react-hot-toast'

interface NewAlbum {
  id: string
  title: string
  description: string
  createdBy: string
  createdAt: string
  isPublic: boolean
  memberIds: string[]
  photoIds: string[]
  invitedEmails: string[]
  user: {
    id: string
    fullName: string
    email: string
    photoURL: string
  }
  totalMembers: number
  totalPhotos: number
  totalInvited: number
}

interface ChartData {
  date: string
  fullDate: string
  count: number
}

export const NewAlbumsChart = () => {
  const [albums, setAlbums] = useState<NewAlbum[]>([])
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('7d')
  const [summary, setSummary] = useState<any>(null)

  useEffect(() => {
    const fetchNewAlbums = async () => {
      try {
        setLoading(true)
        const data = await analyticsService.getNewAlbums(selectedPeriod)
        
        setAlbums(data.albums)
        setSummary(data.summary)

        // Create chart data from albums
        const albumsByDate = new Map<string, number>()
        data.albums.forEach(album => {
          const dateKey = new Date(album.createdAt).toISOString().split('T')[0]
          albumsByDate.set(dateKey, (albumsByDate.get(dateKey) || 0) + 1)
        })

        // Generate chart data for all dates in range
        const chartDataArray: ChartData[] = []
        const startDate = new Date(data.summary.dateRange.start)
        const endDate = new Date(data.summary.dateRange.end)
        
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split('T')[0]
          chartDataArray.push({
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: dateKey,
            count: albumsByDate.get(dateKey) || 0
          })
        }

        setChartData(chartDataArray)
      } catch (error: any) {
        console.error('Failed to fetch new albums:', error)
        const errorMessage = error.response?.data?.error || 'Failed to load new albums data'
        toast.error(errorMessage)
        setAlbums([])
        setChartData([])
        setSummary(null)
      } finally {
        setLoading(false)
      }
    }

    fetchNewAlbums()
  }, [selectedPeriod])

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">New Albums</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="7d">7d</option>
            <option value="30d">30d</option>
            <option value="90d">90d</option>
          </select>
        </div>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">New Albums</h3>
          {summary && (
            <div className="flex items-center mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {summary.totalAlbums}
              </span>
              <span className="ml-2 text-sm font-medium text-gray-500">
                avg {summary.averageAlbumsPerDay}/day
              </span>
            </div>
          )}
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="7d">7d</option>
          <option value="30d">30d</option>
          <option value="90d">90d</option>
        </select>
      </div>

      {chartData.length > 0 ? (
        <div className="h-48 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Date', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip 
                formatter={(value: number) => [value.toLocaleString(), 'New Albums']}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0] && payload[0].payload.fullDate) {
                    const date = new Date(payload[0].payload.fullDate)
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    }
                  }
                  return label
                }}
                labelStyle={{ color: '#374151' }}
              />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke="#f59e0b" 
                fill="#f59e0b"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-gray-500 mb-6">
          No new albums data available
        </div>
      )}

      {/* Recent Albums List */}
      {albums.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Albums</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {albums.map((album) => (
              <div key={album.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-lg bg-orange-300 flex items-center justify-center">
                    <span className="text-sm font-medium text-orange-600">
                      📷
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {album.title}
                  </p>
                                     <div className="flex items-center space-x-2">
                     <p className="text-xs text-gray-500 truncate">
                       by {album.user.fullName}
                     </p>
                     <span className="text-xs text-gray-400">
                       {album.totalPhotos} photos
                     </span>
                     <span className="text-xs text-blue-400">
                       {album.totalMembers} members
                     </span>
                     {album.isPublic && (
                       <span className="text-xs bg-green-100 text-green-800 px-1 rounded">
                         Public
                       </span>
                     )}
                   </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-500">
                    {new Date(album.createdAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
} 